import { useAtomValue } from "@effect/atom-react";
import {
  connectionTargetSharesClientMachine,
  type ConnectionTarget,
} from "@afar-research/client-runtime/connection";
import { resolveEnvironmentSettingsSelection } from "@afar-research/client-runtime/environment";
import type { EnvironmentId } from "@afar-research/contracts";
import { useMemo } from "react";

import { hostEnvironment } from "../connection/hostEnvironment";
import { useClientSettings } from "../hooks/useSettings";

export interface EnvironmentSettingsCapabilities {
  readonly canEditHostSettings: boolean;
  readonly canDeleteArchivedThreads: boolean;
  /** This client runs on the Environment's own computer, so its System Settings are the ones that matter. */
  readonly sharesClientMachine: boolean;
}

export interface EnvironmentSettingsScope extends EnvironmentSettingsCapabilities {
  readonly environmentId: EnvironmentId;
  readonly label: string;
}

export function resolveEnvironmentSettingsTarget(
  selectedEnvironmentId: EnvironmentId | null,
  targets: ReadonlyMap<EnvironmentId, ConnectionTarget>,
): ConnectionTarget | null {
  return resolveEnvironmentSettingsSelection(selectedEnvironmentId, targets)?.target ?? null;
}

/** All Environment-owned Settings surfaces use the same device-local selection. */
export function resolveEnvironmentCatalogSettingsTarget(
  selectedEnvironmentId: EnvironmentId | null,
  targets: ReadonlyMap<EnvironmentId, ConnectionTarget>,
): ConnectionTarget | null {
  return resolveEnvironmentSettingsTarget(selectedEnvironmentId, targets);
}

export function environmentSettingsCapabilities(
  target: ConnectionTarget,
): EnvironmentSettingsCapabilities {
  const isLocalHost = target._tag === "PrimaryConnectionTarget";
  return {
    canEditHostSettings: isLocalHost,
    canDeleteArchivedThreads: isLocalHost,
    sharesClientMachine: connectionTargetSharesClientMachine(target),
  };
}

function useResolvedEnvironmentSettingsScope(
  resolveTarget: (
    selectedEnvironmentId: EnvironmentId | null,
    targets: ReadonlyMap<EnvironmentId, ConnectionTarget>,
  ) => ConnectionTarget | null,
): EnvironmentSettingsScope | null {
  const selectedEnvironmentId = useClientSettings(
    (settings) => settings.environmentSettingsSelectionId,
  );
  const catalog = useAtomValue(hostEnvironment.environmentEntriesValueAtom);
  return useMemo(() => {
    const targets = new Map(
      [...catalog.entries].map(([environmentId, entry]) => [environmentId, entry.target] as const),
    );
    const target = resolveTarget(selectedEnvironmentId, targets);
    if (target === null) {
      return null;
    }
    return {
      environmentId: target.environmentId,
      label: target.label,
      ...environmentSettingsCapabilities(target),
    };
  }, [catalog.entries, resolveTarget, selectedEnvironmentId]);
}

export function useEnvironmentSettingsScope(): EnvironmentSettingsScope | null {
  return useResolvedEnvironmentSettingsScope(resolveEnvironmentSettingsTarget);
}

export function useEnvironmentCatalogSettingsScope(): EnvironmentSettingsScope | null {
  return useResolvedEnvironmentSettingsScope(resolveEnvironmentCatalogSettingsTarget);
}
